import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Trash2 } from 'lucide-react';
import { getOutboundCallUrl, getOutboundCallBaseUrl, getCurrentUserIdForCalls } from '../lib/apiConfig';

// Batch dialer config
const LANE_COUNT = 30;                  // total concurrent lanes (15 per line across the 2-number pool)
const MAX_LANES = 30;
const DIAL_SPACING_MS = 1300;          // spacing between dials per from-number (~1 call/sec each)
const POLL_INTERVAL_MS = 3000;         // how often to poll a live call's status
const MAX_CALL_WAIT_MS = 15 * 60 * 1000; // safety cap: stop waiting on a call after 15 min

// Caller-ID pool ("lines") for outbound calls. Lanes are assigned a number round-robin,
// and each number is throttled independently, so throughput scales with the pool size.
const FROM_NUMBERS = ['+15165187195', '+19145300981'];
const DEFAULT_FROM_NUMBER = FROM_NUMBERS[0]; // single-call (non-batch) path uses the first line


const ContactsImport = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState('');
  const [calling, setCalling] = useState(null);

  // Batch dialer state: 3 lanes, each sequentially dialing its own slice of the contacts.
  const [batchRunning, setBatchRunning] = useState(false);
  const [laneCount, setLaneCount] = useState(LANE_COUNT); // how many concurrent lanes (1–8), tunable per run
  const [lanes, setLanes] = useState([]); // {id, total, done, current, status, results:[]}
  const stopRef = useRef(false);          // set true to halt all lanes after their current call
  const dialGatesRef = useRef({});        // per-from-number dial gates: { [fromNumber]: Promise }
  const runSeqRef = useRef(0);            // bumped each Start; lanes from an older run stop dialing

  // Shared "called" record, DB-backed (survives refresh AND is the same for every operator/device):
  // phone -> { outcome, at }. Sourced from /api/opinion-contacts (port 3016).
  const [calledMap, setCalledMap] = useState({});
  const [hideCalled, setHideCalled] = useState(false);
  const [markN, setMarkN] = useState(200);
  const [page, setPage] = useState(0); // table pagination (roster can be thousands of rows)
  const calledMapRef = useRef({});

  const calledApiBase = process.env.REACT_APP_AGENT_API_URL || '';
  const calledAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  // Load the roster + per-contact called status from the DB on mount.
  useEffect(() => {
    (async () => {
      setLoadingContacts(true);
      try {
        const res = await fetch(`${calledApiBase}/api/opinion-contacts`, { headers: calledAuthHeaders() });
        const data = await res.json();
        if (data.success && Array.isArray(data.contacts)) {
          const withId = data.contacts.map((c, i) => ({ ...c, id: c.phone || String(i) }));
          setContacts(withId);
          const map = {};
          withId.forEach((c) => { if (c.called) map[c.phone] = { outcome: c.outcome || 'called', at: null }; });
          setCalledMap(map);
          calledMapRef.current = map;
        }
      } catch (_) { /* offline — start empty */ }
      setLoadingContacts(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark one number called: update UI immediately, persist to the DB (fire-and-forget).
  const markCalled = (phone, outcome) => {
    const p = String(phone || '').replace(/\D/g, '');
    if (!p) return;
    setCalledMap((prev) => {
      const next = { ...prev, [p]: { outcome: outcome || 'called', at: Date.now() } };
      calledMapRef.current = next;
      return next;
    });
    fetch(`${calledApiBase}/api/opinion-contacts/called`, {
      method: 'POST', headers: calledAuthHeaders(), body: JSON.stringify({ phone: p, outcome }),
    }).catch(() => {});
  };

  const resetCalled = async () => {
    if (!window.confirm('Clear all “called” marks for everyone? Numbers will be dialable again.')) return;
    setCalledMap({});
    calledMapRef.current = {};
    try {
      await fetch(`${calledApiBase}/api/opinion-contacts/reset`, { method: 'POST', headers: calledAuthHeaders() });
    } catch (_) {}
  };

  const removeCalled = () => {
    const map = calledMapRef.current;
    setContacts((prev) => prev.filter((c) => !map[c.phone]));
  };

  // Mark the first N contacts (in list order) as already called — e.g. a batch dialed elsewhere.
  const markFirstNCalled = () => {
    const n = Math.max(0, Math.min(Number(markN) || 0, contacts.length));
    if (!n) return;
    const phones = contacts.slice(0, n).map((c) => c.phone).filter(Boolean);
    setCalledMap((prev) => {
      const next = { ...prev };
      phones.forEach((p) => { if (!next[p]) next[p] = { outcome: 'pre-marked', at: Date.now() }; });
      calledMapRef.current = next;
      return next;
    });
    fetch(`${calledApiBase}/api/opinion-contacts/called/bulk`, {
      method: 'POST', headers: calledAuthHeaders(), body: JSON.stringify({ phones, outcome: 'pre-marked' }),
    }).catch(() => {});
  };

  // Check authentication
  useEffect(() => {
    const authenticated = sessionStorage.getItem('opinionServiceAuthenticated');
    if (!authenticated) {
      navigate('/opinion-service-login');
    }
  }, [navigate]);

  // Fetch available scripts
  useEffect(() => {
    const fetchScripts = async () => {
      try {
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_AGENT_API_URL || '';

        const response = await fetch(`${apiBase}/api/scripts`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const data = await response.json();
        if (data.success) {
          // Filter to show only Opinion Services scripts
          const opinionScripts = data.scripts.filter(s => s.ownerId === 'opinion-service-global');
          setScripts(opinionScripts);
        }
      } catch (err) {
        console.error('Error fetching scripts:', err);
      }
    };

    fetchScripts();
  }, []);

  // Remove a contact
  const removeContact = (id) => {
    setContacts(contacts.filter(c => c.id !== id));
  };

  // Clear all contacts
  const clearAll = () => {
    if (window.confirm('Clear all contacts?')) {
      setContacts([]);
    }
  };

  // Make a call — uses the SAME endpoint as the "Start TEN call" test in ScriptManager
  // (POST /outbound-call on the V2 server, port 3017, with useTen:true). The old /api/make-call
  // endpoint never existed on the backend, so calls from here always failed.
  const handleCall = async (contact) => {
    if (!selectedScript) {
      alert('Please select a script first');
      return;
    }
    // The outbound endpoint expects the full script object (scriptData with _id), not a bare id.
    const baseScript = scripts.find((s) => s._id === selectedScript);
    if (!baseScript) {
      alert('Selected script not found. Please re-select a script.');
      return;
    }
    // Attach the contact's name so the script can personalize (e.g. {{firstName}}), mirroring
    // ScriptManager's test-call behavior.
    const scriptData = {
      ...baseScript,
      firstName: contact.firstName || baseScript.firstName || null,
      lastName: contact.lastName || baseScript.lastName || null,
    };

    setCalling(contact.id);
    try {
      const token = localStorage.getItem('token');
      const placedByUserId = getCurrentUserIdForCalls();

      const body = {
        to: contact.phone,
        from: DEFAULT_FROM_NUMBER,
        stack: 'kokoro-vad',
        preferBodyScript: true,
        ...(placedByUserId ? { placedByUserId } : {}),
        scriptData,
        fileId: null,
        // Use the script's configured voice; server/TEN falls back if absent.
        ...(baseScript.settings?.voiceId ? { voiceId: baseScript.settings.voiceId } : {}),
        ttsProvider: 'kokoro',
        // Route audio through the TEN framework server (same as the test-panel TEN button).
        useTen: true,
        // Omit firstMessage so the server uses the script's FIRST MESSAGE line or default intro.
      };

      const response = await fetch(getOutboundCallUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        markCalled(contact.phone, 'called');
        alert(`Call started to ${contact.phone}. Call SID: ${data.callSid}`);
      } else {
        alert(`Error: ${data.message || 'Failed to initiate call'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setCalling(null);
    }
  };

  // ---------- Batch dialer ----------
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const updateLane = (laneId, fn) =>
    setLanes((prev) => prev.map((l) => (l.id === laneId ? fn(l) : l)));

  // Place one call (returns { ok, callSid, message }); shared shape with handleCall.
  const placeCall = async (contact, fromNumber = DEFAULT_FROM_NUMBER) => {
    const baseScript = scripts.find((s) => s._id === selectedScript);
    if (!baseScript) return { ok: false, message: 'Script not found' };
    const token = localStorage.getItem('token');
    const placedByUserId = getCurrentUserIdForCalls();
    const scriptData = {
      ...baseScript,
      firstName: contact.firstName || baseScript.firstName || null,
      lastName: contact.lastName || baseScript.lastName || null,
    };
    const body = {
      to: contact.phone,
      from: fromNumber,
      stack: 'kokoro-vad',
      preferBodyScript: true,
      ...(placedByUserId ? { placedByUserId } : {}),
      scriptData,
      fileId: null,
      ...(baseScript.settings?.voiceId ? { voiceId: baseScript.settings.voiceId } : {}),
      ttsProvider: 'kokoro',
      useTen: true,
    };
    try {
      const res = await fetch(getOutboundCallUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.success) return { ok: true, callSid: data.callSid };
      return { ok: false, message: data.message || 'Failed to start' };
    } catch (err) {
      return { ok: false, message: err.message };
    }
  };

  // Throttle dials per from-number so each line isn't hit faster than ~1/sec, while
  // different lines dial in parallel. Lanes sharing a number queue behind that number's gate.
  const throttledDial = (contact, fromNumber) => {
    const prev = dialGatesRef.current[fromNumber] || Promise.resolve();
    const run = prev.then(() => placeCall(contact, fromNumber));
    dialGatesRef.current[fromNumber] = run.then(() => sleep(DIAL_SPACING_MS), () => sleep(DIAL_SPACING_MS));
    return run;
  };

  // Poll Twilio (via V2) until the call reaches a terminal state; returns the final status.
  // NOT gated on stopRef: pressing Stop halts new dials but lets a call already in progress
  // run to completion and record its real outcome (only the safety time cap ends the wait).
  const waitForCallDone = async (callSid) => {
    const token = localStorage.getItem('token');
    const start = Date.now();
    const base = getOutboundCallBaseUrl();
    while (Date.now() - start < MAX_CALL_WAIT_MS) {
      await sleep(POLL_INTERVAL_MS);
      try {
        const res = await fetch(`${base}/api/call-sid-status/${callSid}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (data.success && data.ended) return data.status || 'completed';
      } catch (_) { /* transient — keep polling */ }
    }
    return 'unknown';
  };

  // One lane: dial its queue one contact at a time, advancing when each call ends.
  const laneRunner = async (laneId, queue, fromNumber, runSeq) => {
    for (let i = 0; i < queue.length; i++) {
      if (stopRef.current || runSeqRef.current !== runSeq) break;
      const contact = queue[i];
      updateLane(laneId, (l) => ({ ...l, current: contact, status: 'dialing' }));
      const dial = await throttledDial(contact, fromNumber);
      if (!dial.ok) {
        markCalled(contact.phone, 'start-failed');
        updateLane(laneId, (l) => ({
          ...l, done: l.done + 1, status: 'error',
          results: [...l.results, { contact, outcome: 'start-failed' }],
        }));
        continue;
      }
      // Mark called as soon as the call is placed (before waiting) so a Stop mid-dial can't
      // leave it unmarked and cause a re-dial on the next run. Outcome is updated when it ends.
      markCalled(contact.phone, 'in-progress');
      updateLane(laneId, (l) => ({ ...l, status: 'in call' }));
      const outcome = await waitForCallDone(dial.callSid);
      markCalled(contact.phone, outcome);
      updateLane(laneId, (l) => ({
        ...l, done: l.done + 1, status: 'idle',
        results: [...l.results, { contact, outcome, callSid: dial.callSid }],
      }));
    }
    updateLane(laneId, (l) => ({ ...l, current: null, status: 'finished' }));
  };

  const startBatch = async () => {
    if (!selectedScript) { alert('Please select a script first'); return; }
    if (!contacts.length) { alert('No contacts to call'); return; }
    // Skip numbers already dialed (so re-running resumes instead of re-calling).
    const pending = contacts.filter((c) => !calledMapRef.current[c.phone]);
    if (!pending.length) {
      alert('All contacts have already been called. Use “Reset marks” to dial them again.');
      return;
    }
    stopRef.current = false;
    dialGatesRef.current = {};
    const myRun = ++runSeqRef.current; // any still-finishing lanes from a prior run will stop
    // Round-robin split so all lanes finish around the same time.
    const n = Math.max(1, Math.min(MAX_LANES, laneCount));
    const queues = Array.from({ length: n }, () => []);
    pending.forEach((c, idx) => queues[idx % n].push(c));
    // Assign each lane a from-number round-robin across the pool.
    const laneFrom = (idx) => FROM_NUMBERS[idx % FROM_NUMBERS.length];
    setLanes(queues.map((q, idx) => ({ id: idx, total: q.length, done: 0, current: null, status: 'starting', results: [], from: laneFrom(idx) })));
    setBatchRunning(true);
    await Promise.all(queues.map((q, idx) => laneRunner(idx, q, laneFrom(idx), myRun)));
    setBatchRunning(false);
  };

  const stopBatch = () => {
    stopRef.current = true;
    setBatchRunning(false);
  };

  const calledCount = contacts.reduce((n, c) => n + (calledMap[c.phone] ? 1 : 0), 0);
  const visibleContacts = hideCalled ? contacts.filter((c) => !calledMap[c.phone]) : contacts;
  const PAGE_SIZE = 100;
  const totalPages = Math.max(1, Math.ceil(visibleContacts.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pagedContacts = visibleContacts.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-3xl font-bold text-gray-900">📋 Import Contacts</h1>
          <p className="text-gray-600 text-sm mt-1">Import and manage contacts for calling campaigns</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Script Selection */}
        <div className="bg-white rounded-lg shadow p-8 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Script for Calling</h2>
          <select
            value={selectedScript}
            onChange={(e) => setSelectedScript(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">-- Choose a script --</option>
            {scripts.map(script => (
              <option key={script._id} value={script._id}>
                {script.scriptName}
              </option>
            ))}
          </select>
          {scripts.length === 0 && (
            <p className="text-sm text-gray-600 mt-2">No Opinion Services scripts available. Create one first.</p>
          )}
        </div>

        {/* Batch Dialer */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Batch Call — {laneCount} lanes</h2>
              <p className="text-sm text-gray-600 mt-1">
                Splits the {contacts.length} contacts across {laneCount} lanes. Each lane dials the next
                number as soon as its current call ends ({laneCount} calls run at once).
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {FROM_NUMBERS.length} caller lines: {FROM_NUMBERS.join(', ')} (assigned to lanes round-robin).
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <label className="flex items-center gap-1 text-sm text-gray-700">
                Lanes
                <select
                  value={laneCount}
                  onChange={(e) => setLaneCount(Number(e.target.value))}
                  disabled={batchRunning}
                  className={`border border-gray-300 rounded px-2 py-2 text-sm ${batchRunning ? 'bg-gray-100 text-gray-400' : ''}`}
                >
                  {Array.from({ length: MAX_LANES }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              {!batchRunning ? (
                <button
                  onClick={startBatch}
                  disabled={!selectedScript || contacts.length === 0}
                  className={`px-5 py-2 rounded-lg font-medium transition ${
                    selectedScript && contacts.length > 0
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Start batch
                </button>
              ) : (
                <button
                  onClick={stopBatch}
                  className="px-5 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 transition"
                >
                  Stop
                </button>
              )}
            </div>
          </div>

          {lanes.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-5">
              {lanes.map((lane) => (
                <div key={lane.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900">
                      Lane {lane.id + 1}
                      {lane.from && <span className="ml-2 text-xs font-normal text-gray-400">{lane.from}</span>}
                    </span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-100 text-blue-700 capitalize">
                      {lane.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-2">{lane.done} / {lane.total} done</div>
                  <div className="w-full bg-gray-200 h-2 rounded mt-1 overflow-hidden">
                    <div
                      className="bg-green-500 h-2 rounded transition-all"
                      style={{ width: `${lane.total ? (lane.done / lane.total) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="text-sm text-gray-800 mt-2 truncate">
                    {lane.current
                      ? `📞 ${[lane.current.firstName, lane.current.lastName].filter(Boolean).join(' ')} — ${lane.current.phone}`
                      : (lane.status === 'finished' ? '✓ Lane complete' : '—')}
                  </div>
                  {lane.results.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      Last: {[lane.results[lane.results.length - 1].contact.phone, lane.results[lane.results.length - 1].outcome].join(' → ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contacts Table */}
        {contacts.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Header with info */}
            <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Imported Contacts</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {contacts.length} contacts · <span className="text-green-700 font-medium">{calledCount} called</span> · {contacts.length - calledCount} remaining
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none px-2">
                  <input
                    type="checkbox"
                    checked={hideCalled}
                    onChange={(e) => setHideCalled(e.target.checked)}
                    className="w-4 h-4 accent-blue-600 cursor-pointer"
                  />
                  Hide called
                </label>
                <div className="flex items-center gap-1 border-l border-gray-200 pl-2">
                  <span className="text-sm text-gray-600">Mark first</span>
                  <input
                    type="number"
                    min="0"
                    value={markN}
                    onChange={(e) => setMarkN(e.target.value)}
                    className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <button
                    onClick={markFirstNCalled}
                    className="px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition"
                  >
                    as called
                  </button>
                </div>
                <button
                  onClick={removeCalled}
                  disabled={calledCount === 0}
                  className={`px-3 py-2 rounded-lg text-sm transition ${calledCount ? 'text-red-600 hover:bg-red-50' : 'text-gray-300 cursor-not-allowed'}`}
                >
                  Remove called ({calledCount})
                </button>
                <button
                  onClick={resetCalled}
                  disabled={calledCount === 0}
                  className={`px-3 py-2 rounded-lg text-sm transition ${calledCount ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-300 cursor-not-allowed'}`}
                >
                  Reset marks
                </button>
                <button
                  onClick={clearAll}
                  className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition text-sm"
                >
                  <Trash2 size={18} />
                  Clear All
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Phone Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Voter ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pagedContacts.map(contact => {
                    const called = calledMap[contact.phone];
                    return (
                    <tr key={contact.id} className={called ? 'bg-gray-50/70 text-gray-400' : 'hover:bg-gray-50'}>
                      <td className={`px-6 py-4 text-sm ${called ? 'text-gray-400' : 'text-gray-900'}`}>
                        {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || '-'}
                      </td>
                      <td className={`px-6 py-4 text-sm font-medium ${called ? 'text-gray-400' : 'text-gray-900'}`}>{contact.phone}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{contact.voterId || '-'}</td>
                      <td className="px-6 py-4 text-sm">
                        {called ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            ✓ Called{called.outcome && called.outcome !== 'called' ? ` · ${called.outcome}` : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Not called</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right text-sm space-x-2">
                        <button
                          onClick={() => handleCall(contact)}
                          disabled={!selectedScript || calling === contact.id}
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition ${
                            selectedScript && calling !== contact.id
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          <Phone size={16} />
                          {calling === contact.id ? 'Calling...' : 'Call'}
                        </button>
                        <button
                          onClick={() => removeContact(contact.id)}
                          className="inline-flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {visibleContacts.length > PAGE_SIZE && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 text-sm text-gray-600">
                <span>
                  Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, visibleContacts.length)} of {visibleContacts.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={safePage === 0}
                    className={`px-3 py-1 rounded ${safePage === 0 ? 'text-gray-300' : 'text-blue-600 hover:bg-blue-50'}`}
                  >
                    Prev
                  </button>
                  <span>Page {safePage + 1} / {totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={safePage >= totalPages - 1}
                    className={`px-3 py-1 rounded ${safePage >= totalPages - 1 ? 'text-gray-300' : 'text-blue-600 hover:bg-blue-50'}`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {loadingContacts && contacts.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Loading contacts…</div>
        )}

      </main>
    </div>
  );
};

export default ContactsImport;
